import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, Sun, Zap, Shield, Sparkles } from "lucide-react";
import { PageHero, FeatureList } from "../components/PageShells";
import SavingsCalculator from "../components/SavingsCalculator";
import LogoMarquee from "../components/LogoMarquee";

const packages = [
  {
    id: "starter",
    name: "Starter",
    panels: 6,
    wp: 2280,
    production: "2.100",
    price: "€ 3.290",
    monthly: "€ 38",
    badge: null,
    color: "from-slate-50 to-white",
    features: [
      "6 full-black 380Wp panelen",
      "Premium omvormer (SolarEdge)",
      "App-monitoring",
      "10 jaar productgarantie",
    ],
  },
  {
    id: "comfort",
    name: "Comfort",
    panels: 12,
    wp: 4560,
    production: "4.300",
    price: "€ 5.890",
    monthly: "€ 68",
    badge: "Populair",
    color: "from-brand-soft/60 to-white",
    features: [
      "12 full-black 380Wp panelen",
      "Per-paneel optimizers",
      "Slim energiebeheer (AI)",
      "25 jaar paneelgarantie",
      "Gratis schouwen aan huis",
    ],
  },
  {
    id: "premium",
    name: "Premium",
    panels: 18,
    wp: 7290,
    production: "6.500",
    price: "€ 8.490",
    monthly: "€ 98",
    badge: "Beste deal",
    color: "from-brand/10 to-white",
    features: [
      "18 high-yield 405Wp panelen",
      "Enphase micro-inverters",
      "Premium installatie & afwerking",
      "Klaar voor uitbreiding met batterij",
      "Voorrang in installatieplanning",
    ],
  },
  {
    id: "max",
    name: "Max",
    panels: 24,
    wp: 10800,
    production: "9.600",
    price: "€ 11.890",
    monthly: "€ 137",
    badge: "Max rendement",
    color: "from-navy-900/5 to-white",
    features: [
      "24 premium 450Wp panelen",
      "SolarEdge HD-Wave omvormer",
      "Geschikt voor warmtepomp + EV",
      "3-fase aansluiting inbegrepen",
      "Onbeperkte support eerste jaar",
    ],
  },
];

const SolarPackages = () => {
  const [selected, setSelected] = useState("comfort");
  const active = packages.find((p) => p.id === selected);

  return (
    <section className="section-pad bg-slate-50/70" data-testid="solar-packages">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-3">Zonnepanelen kopen</p>
          <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter text-navy-900 text-balance">Vaste pakketprijzen. Geen verrassingen.</h2>
          <p className="mt-5 text-slate-600 text-lg">Inclusief installatie, omvormer, montage en oplevering. Klaar voor uitbreiding met thuisbatterij.</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {packages.map((p, i) => (
            <motion.button
              key={p.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              onClick={() => setSelected(p.id)}
              className={`relative text-left bg-gradient-to-br ${p.color} rounded-3xl border-2 p-7 transition-all duration-300 hover:-translate-y-1 ${
                selected === p.id
                  ? "border-brand shadow-[0_20px_50px_rgba(255,47,125,0.18)]"
                  : "border-slate-200 hover:border-brand/40"
              }`}
              data-testid={`package-${p.id}`}
            >
              {p.badge && (
                <span className="absolute -top-3 left-7 inline-flex px-3 py-1 rounded-full gradient-pink text-white text-xs font-bold tracking-wide shadow-[0_4px_14px_rgba(255,47,125,0.4)]">
                  {p.badge}
                </span>
              )}
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-brand">
                  <Sun size={18} />
                </div>
                <span className="text-xs uppercase tracking-widest font-bold text-slate-500">{p.name}</span>
              </div>
              <div className="font-heading text-4xl font-black text-navy-900 mb-1">{p.panels}</div>
              <div className="text-sm text-slate-500 mb-5">panelen · {(p.wp / 1000).toFixed(1)} kWp</div>

              <ul className="space-y-2 mb-6 min-h-[140px]">
                {p.features.slice(0, 4).map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                    <span className="w-4 h-4 rounded-full bg-brand-soft text-brand flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check size={10} strokeWidth={3} />
                    </span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <div className="pt-5 border-t border-slate-100">
                <div className="text-xs text-slate-500 mb-0.5">Vanaf (incl. installatie)</div>
                <div className="font-heading text-2xl font-black text-navy-900">{p.price}</div>
                <div className="text-xs text-brand-deep font-semibold mt-1">of {p.monthly} per maand</div>
              </div>
            </motion.button>
          ))}
        </div>

        <motion.div
          key={active.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-10 bg-navy-900 text-white rounded-3xl p-7 lg:p-9 grid lg:grid-cols-3 gap-7 items-center relative overflow-hidden"
          data-testid="package-summary"
        >
          <div className="blob-pink absolute -top-16 -right-10 opacity-50" />
          <div className="relative">
            <div className="text-xs uppercase tracking-widest text-brand font-bold mb-1">Geselecteerd pakket</div>
            <div className="font-heading text-3xl font-black">{active.name} · {active.panels} panelen</div>
            <div className="text-slate-300 mt-1">~ {active.production} kWh/jaar verwachte opbrengst</div>
          </div>
          <div className="relative grid grid-cols-3 gap-4">
            <Stat label="Opbrengst" value={`${active.production} kWh`} />
            <Stat label="Vermogen" value={`${(active.wp / 1000).toFixed(1)} kWp`} />
            <Stat label="Terugverdien" value={`~ 6 jr`} />
          </div>
          <div className="relative flex flex-col gap-3">
            <Link
              to="/contact"
              state={{ calculator: { panels: active.panels, package: active.name, estimated_savings: Math.round(parseInt(active.production.replace(".", "")) * 0.34), source: `solar_package_${active.id}` } }}
              className="btn-pink !text-white w-full"
              data-testid="package-cta"
            >
              Bestel {active.name} <ArrowRight size={18} />
            </Link>
            <a href="#calculator" className="text-center text-sm text-slate-300 hover:text-brand transition-colors">Of bereken eerst je besparing →</a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const Stat = ({ label, value }) => (
  <div>
    <div className="text-xs uppercase tracking-widest text-slate-400 mb-1">{label}</div>
    <div className="font-heading font-black text-lg">{value}</div>
  </div>
);

const WhyBuy = () => (
  <section className="section-pad" data-testid="solar-why">
    <div className="max-w-7xl mx-auto px-5 lg:px-8">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand mb-3">Waarom kopen bij Helios Bright</p>
        <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter text-navy-900 text-balance">Premium kwaliteit. Geen verkooppraatjes.</h2>
      </div>
      <div className="grid md:grid-cols-3 gap-5">
        {[
          { icon: Zap, title: "Vaste pakketprijzen", desc: "Geen offerte-spelletjes. Je weet vooraf wat je betaalt, installatie inbegrepen." },
          { icon: Shield, title: "25 jaar productgarantie", desc: "Op alle panelen. 10 jaar op omvormer & montage. Door eigen Helios-team." },
          { icon: Sparkles, title: "Klaar voor batterij", desc: "Elke installatie is voorbereid op uitbreiding met thuisbatterij, zonder extra werk later." },
        ].map((b) => (
          <div key={b.title} className="bg-white rounded-3xl border border-slate-200 p-7 hover:border-brand/40 hover:-translate-y-1 transition-all">
            <div className="w-12 h-12 rounded-xl bg-brand-soft text-brand flex items-center justify-center mb-5">
              <b.icon size={22} />
            </div>
            <h3 className="font-heading font-bold text-xl text-navy-900 mb-2">{b.title}</h3>
            <p className="text-slate-600 text-sm leading-relaxed">{b.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Zonnepanelen = () => (
  <main data-testid="solar-page">
    <PageHero
      eyebrow="Zonnepanelen"
      title="Hoogrendement zonnepanelen die langer meegaan."
      subtitle="Premium full-black panelen met een rendement tot 22%, in combinatie met merkomvormers en optimizers. Vaste pakketprijzen, gecertificeerde monteurs."
      image="https://images.unsplash.com/photo-1777104631979-d57208fa7cf7?crop=entropy&cs=srgb&fm=jpg&q=85"
    />

    <SolarPackages />

    <FeatureList
      title="Premium zonne-installaties op maat"
      items={[
        { title: "Tot 460Wp per paneel", desc: "Hoog rendement, minimale dakbezetting." },
        { title: "25 jaar productgarantie", desc: "Op panelen van topmerken." },
        { title: "SolarEdge / Enphase", desc: "Optimizers per paneel voor maximaal rendement." },
        { title: "Esthetisch geïntegreerd", desc: "Full-black, geen zichtbare frames." },
        { title: "Combineer met batterij", desc: "Optimaliseer eigen verbruik tot 90%." },
        { title: "Snelle installatie", desc: "Meeste daken binnen 1 dag opgeleverd." },
      ]}
    />

    <WhyBuy />
    <SavingsCalculator />
    <LogoMarquee />
  </main>
);

export default Zonnepanelen;
